/**
 * انتقالِ بدهی به ماهِ بعد — در ویرایشِ نفر‌به‌نفرِ حقوق (v2.109.0).
 *
 * خواستهٔ کاربر (۱۴۰۵/۰۷/۰۱): «یه بخشی از بدهیش رو… انتقال بدیم به ماه بعدش، انگار وام کوتاه‌مدت؛
 * مثلاً یه مقدار بمونه تو همین ماه و از حسابش کم بشه، مابقیش بره تو ماه جدید».
 *
 * هر انتقال دو سندِ به‌هم‌بسته در دفترِ مالی می‌سازد (بستانکارِ این ماه + «بدهی قبلی» اولِ ماهِ بعد)،
 * پس ماندهٔ واقعیِ شخص عوض نمی‌شود؛ فقط زمانِ کسر. ذخیره فوری است و حقوقِ همین نفر دوباره حساب می‌شود.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button, Input, Popconfirm, Space, Tag, Typography, message } from "antd";
import { DeleteOutlined, SwapRightOutlined } from "@ant-design/icons";
import { createDebtDeferral, deleteDebtDeferral, getDebtDeferrals } from "../api/client";
import { MoneyInput, formatMoney } from "../utils/money";
import { toJalali } from "../utils/dateUtils";
import { faDigits } from "../utils/faNumberFormat";

const { Text } = Typography;

export default function DebtDeferralPanel({ periodId, employeeId, runGuarded, onChanged }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState("move");      // move = «چقدر برود» | keep = «چقدر بماند»
  const [value, setValue] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!periodId || !employeeId) { setData(null); return; }
    try { setData((await getDebtDeferrals(periodId, employeeId)).data); } catch { setData(null); }
  }, [periodId, employeeId]);
  useEffect(() => { load(); setValue(null); setNote(""); }, [load]);

  if (!data) return null;
  const remaining = Math.max(0, Number(data.remaining || 0));
  // «بماند» = چقدر همین ماه کسر شود؛ بقیه منتقل می‌شود
  // فیلدِ خالی یعنی «هنوز چیزی نگفته» — نه «همه را منتقل کن»
  const moveAmount = value == null || value === "" ? 0
    : (mode === "move" ? Number(value) : Math.max(0, remaining - Number(value)));

  const save = async () => {
    if (!(moveAmount > 0)) return message.warning("مبلغِ انتقال را وارد کنید");
    if (moveAmount > remaining) return message.warning(`حداکثر ${formatMoney(remaining)} ریال قابلِ انتقال است`);
    setBusy(true);
    const ok = await runGuarded(
      (pin) => createDebtDeferral(periodId, employeeId, { amount: moveAmount, note: note || null }, pin),
      (res) => res?.data?.message || "منتقل شد");
    setBusy(false);
    if (ok) { setValue(null); setNote(""); await load(); onChanged?.(); }
  };
  const remove = async (id) => {
    const ok = await runGuarded((pin) => deleteDebtDeferral(periodId, employeeId, id, pin),
      (res) => res?.data?.message || "برداشته شد");
    if (ok) { await load(); onChanged?.(); }
  };

  return (
    <div style={{ border: "1px solid var(--gx-border, #eee)", borderRadius: 8, padding: "8px 12px",
                  margin: "8px 0 12px", background: "var(--gx-surface-2, #fafafa)" }}>
      <Space wrap style={{ marginBottom: 6 }}>
        <b>انتقالِ بدهی به ماهِ بعد</b>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {data.prior_debt > 0 && <>بدهیِ قبلی: <b className="gx-money" style={{ color: "var(--gx-danger, #cf1322)" }}>
            {formatMoney(data.prior_debt)}</b> — </>}
          کسوراتِ دفترِ این ماه: <b className="gx-money">{formatMoney(data.month_deductions)}</b>
          {data.deferred > 0 && <> — منتقل‌شده: <b className="gx-money">{formatMoney(data.deferred)}</b></>}
          {" "}— قابلِ انتقال: <b className="gx-money">{formatMoney(remaining)}</b> ریال
        </Text>
      </Space>
      {/* v2.119.0: ریزِ بدهیِ قبلی (مانده تا پیش از ماه) — در حقوقِ همین ماه کسر می‌شود و قابلِ انتقال است */}
      {data.prior_debt > 0 && (data.prior_items || []).length > 0 && (
        <div style={{ fontSize: 12, marginBottom: 6 }}>
          {(data.prior_items || []).map((it, i) => (
            <div key={i}>
              <Text type="secondary">• {it.label}: </Text>
              <b className="gx-money">{formatMoney(it.amount)}</b>
            </div>
          ))}
        </div>
      )}
      {data.prior_blocked && (
        <Text type="warning" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>{data.prior_blocked}</Text>
      )}
      {remaining > 0 && (
        <Space wrap align="end">
          <Space.Compact>
            <Button type={mode === "move" ? "primary" : "default"} onClick={() => setMode("move")}>این مبلغ برود ماهِ بعد</Button>
            <Button type={mode === "keep" ? "primary" : "default"} onClick={() => setMode("keep")}>این مبلغ همین ماه کسر شود</Button>
          </Space.Compact>
          <MoneyInput value={value} onChange={setValue} style={{ width: 160 }}
            placeholder={mode === "move" ? "مبلغِ انتقال" : "مبلغِ کسرِ این ماه"} />
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="توضیح (اختیاری)"
            style={{ width: 180 }} maxLength={120} />
          <Button icon={<SwapRightOutlined />} loading={busy} disabled={!(moveAmount > 0)} onClick={save}>
            انتقال {moveAmount > 0 ? `${formatMoney(moveAmount)} ریال` : ""}
          </Button>
        </Space>
      )}
      {(data.items || []).map((i) => (
        <div key={i.id} style={{ marginTop: 6, fontSize: 12 }}>
          <Tag color="purple">{i.doc_no}</Tag>
          <b className="gx-money">{formatMoney(i.amount)}</b> ریال → کسر در {toJalali(i.next_date)}
          {i.next_doc_no && <Text type="secondary"> (سندِ {i.next_doc_no})</Text>}
          {i.description && <Text type="secondary"> — {faDigits(i.description)}</Text>}
          <Popconfirm title="انتقال برداشته شود؟" description="مبلغ دوباره از همین ماه کسر می‌شود."
            okText="بله" cancelText="خیر" onConfirm={() => remove(i.id)}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ))}
    </div>
  );
}
